// =========================================
// BY ZY — R2 Media URL Resolution
// Converts relative R2 paths to full URLs.
// Detects media kind (image/video) from extensions.
// =========================================

const R2_BASE = (process.env.REACT_APP_R2_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
const IS_DEV = process.env.NODE_ENV === 'development';

/** Video file extensions that cannot be rendered in an <img> tag. */
const VIDEO_EXTS = /\.(webm|mp4|mov|avi|mkv|m4v|ogv)(\?.*)?$/i;

/** Image file extensions. */
const IMAGE_EXTS = /\.(jpe?g|png|webp|gif|avif|svg|bmp|ico)(\?.*)?$/i;

/**
 * Get the configured R2 public base URL (no trailing slash).
 */
export function getR2BaseUrl() {
  return R2_BASE;
}

/**
 * Returns true if the path looks like a video file (by extension).
 * Used to prevent placing video URLs into <img> tags.
 */
export function isVideoSrc(pathOrUrl) {
  if (!pathOrUrl) return false;
  return VIDEO_EXTS.test(pathOrUrl);
}

/**
 * Returns true if the path looks like an image file (by extension).
 */
export function isImageSrc(pathOrUrl) {
  if (!pathOrUrl) return false;
  return IMAGE_EXTS.test(pathOrUrl);
}

/**
 * Infer the media kind from a path/URL by file extension.
 * Returns 'video' | 'image'. Defaults to 'image' for unknown extensions.
 *
 * Priority: explicit `type`/`kind` field > extension detection > 'image'.
 */
export function inferMediaKind(item) {
  // Trust explicit type/kind field first
  const explicit = item?.type || item?.kind;
  if (explicit === 'video' || explicit === 'image') return explicit;

  // Detect from src extension
  if (item?.src && isVideoSrc(item.src)) return 'video';

  return 'image';
}

/**
 * Resolve a media path to a full URL.
 *
 * Rules:
 * - Falsy / empty string → returns '' (empty string).
 * - Absolute URL (http/https) → returned unchanged.
 * - Leading slash (e.g. /images/foo.jpg) → local public asset, returned as-is.
 * - Relative path (e.g. clients/slug/photo.jpg) → joined with R2_BASE.
 * - If R2_BASE not configured, relative paths returned as-is (dev fallback).
 */
export function toMediaUrl(pathOrUrl) {
  if (!pathOrUrl || pathOrUrl === 'undefined' || pathOrUrl === 'null') return '';
  // Already absolute
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  // Local public asset
  if (pathOrUrl.startsWith('/')) return pathOrUrl;
  // R2 relative path — strip leading slashes to prevent double-slash in URL
  if (R2_BASE) return `${R2_BASE}/${pathOrUrl.replace(/^\/+/, '')}`;
  // No R2 configured — dev warning
  if (IS_DEV) {
    console.warn(`[BY ZY] toMediaUrl: no R2_BASE configured for relative path "${pathOrUrl}"`);
  }
  return pathOrUrl;
}
